import { useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "@/stores/authStore";
import { getSession } from "@/api/auth";
import * as authApi from "@/api/auth";

export function useAuth() {
  const navigate = useNavigate();
  const {
    user,
    tokens,
    isAuthenticated,
    initialized,
    signIn: storeSignIn,
    signOut: storeSignOut,
    setSession,
    initialize,
  } = useAuthStore();

  // On mount, restore session (and auto Dev Login in Vite DEV)
  useEffect(() => {
    if (!initialized) {
      void initialize();
    }
  }, [initialized, initialize]);

  // Verify server-side session validity when we have local tokens.
  // Skip forced sign-out in DEV — auto-login will refresh the session.
  useEffect(() => {
    if (initialized && isAuthenticated && !import.meta.env.DEV) {
      getSession()
        .then((session) => {
          if (!session) {
            storeSignOut();
            navigate("/signin", { replace: true });
          }
        })
        .catch(() => {
          // Network errors are okay — keep the local session
        });
    }
  }, [initialized, isAuthenticated, navigate, storeSignOut]);

  const signIn = useCallback(
    async (email: string, password: string) => {
      const { user, tokens } = await authApi.signIn(email, password);
      storeSignIn(user, tokens);
      navigate("/", { replace: true });
    },
    [navigate, storeSignIn],
  );

  const devSignIn = useCallback(async () => {
    const { user, tokens } = await authApi.devLogin();
    storeSignIn(user, tokens);
    navigate("/", { replace: true });
  }, [navigate, storeSignIn]);

  const signUp = useCallback(
    async (email: string, password: string, name?: string) => {
      const { user, tokens } = await authApi.signUp(email, password, name);
      storeSignIn(user, tokens);
      navigate("/", { replace: true });
    },
    [navigate, storeSignIn],
  );

  const signOut = useCallback(async () => {
    try {
      await authApi.signOut();
    } catch {
      // Proceed with local sign-out even if server call fails
    }
    storeSignOut();
    navigate("/signin", { replace: true });
  }, [navigate, storeSignOut]);

  return {
    user,
    tokens,
    isAuthenticated,
    initialized,
    signIn,
    devSignIn,
    signUp,
    signOut,
  };
}